'use client';
import useV2Position from '@/application/read/migrate/useV2Position';
import useV2Vaults from '@/application/read/migrate/useV2Vaults';
import useAddress from '@/application/read/web3/useAddress';
import { TransactionState } from '@nftx/react';
import { useEffect, useMemo, useState } from 'react';
import useMigrateSlp from '@/application/write/migrate/useMigrateSlp';
import useSignRemoveLiquidity from '@/application/write/migrate/useSignRemoveLiquidity';
import MigrateSlp from './MigrateSlp';
import useVault from '@/application/read/vaults/useVault';
import { useModalContext } from '@/presentation/context/modal';
import MigrateSlpSuccess from './MigrateSlpSuccess';
import { useNetwork } from '@/presentation/context/network';
import Icon, { Icons } from '../../elements/Icon';

export type Props = {
  v3VaultId: string;
  fullRange: boolean;
  positionId: string;
  showXSlpSteps?: boolean;
  minVToken?: bigint;
};

const MigrateSlpContainer = ({
  fullRange,
  positionId,
  v3VaultId,
  showXSlpSteps,
  minVToken,
}: Props) => {
  const { chain } = useNetwork();
  const [success, setSuccess] = useState(false);
  const { closeModal } = useModalContext();
  const { showMigrateModal } = useModalContext();
  const userAddress = useAddress();
  const { data: position, refetch } = useV2Position({
    positionId,
    userAddress,
  });
  const { data: v2Vaults } = useV2Vaults();
  const { data: v3Vault } = useVault({ vaultId: v3VaultId });
  const hasSufficientVToken = useMemo(() => {
    if (!minVToken) {
      return true;
    }
    if (!position) {
      return false;
    }
    return position.vToken >= minVToken - 1000n;
  }, [minVToken, position]);

  const {
    mutate: signRemove,
    data: signData,
    status: signRemoveMutateStatus,
    error: signRemoveError,
  } = useSignRemoveLiquidity();
  const signRemoveStatus = useMemo((): TransactionState => {
    switch (signRemoveMutateStatus) {
      case 'idle':
        return 'None';
      case 'loading':
        return 'Mining';
      case 'error':
        return 'Exception';
      case 'success':
        return 'Success';
      default:
        return 'None';
    }
  }, [signRemoveMutateStatus]);
  const [migrateSlp, { status: migrateStatus, error: migrateError, data }] =
    useMigrateSlp();

  useEffect(() => {
    if (!position || !hasSufficientVToken) {
      const h = setTimeout(() => refetch(), 5000);
      return () => clearTimeout(h);
    }
  }, [hasSufficientVToken, position, refetch]);

  const v2Vault =
    v2Vaults &&
    position &&
    v2Vaults.find((vault) => vault.vaultId === position.vaultId);

  const handleCancel = () => {
    showMigrateModal({ positionId });
  };

  const handleComplete = () => {
    setSuccess(true);
  };

  if (success) {
    return (
      <MigrateSlpSuccess
        chain={chain}
        transaction={data?.transaction}
        onClose={() => closeModal()}
      />
    );
  }

  if (!position || !v2Vault || !v3Vault) {
    return (
      <div className="p-6 flex flex-col items-center justify-center space-y-4">
        <Icon name={Icons.SPINNER_GAP} className="animate-spin" size={24} />
        {showXSlpSteps && <p>{'Preparing your SLP position'}</p>}
      </div>
    );
  }

  if (!hasSufficientVToken) {
    // The user has some SLP already but the xSlp hasn't been added to it yet
    return (
      <div className="flex items-center justify-center text-center p-6">
        <Icon
          name={Icons.SPINNER_GAP}
          className="animate-spin mr-2"
          size={24}
        />
        <span>Syncing SLP balances...</span>
      </div>
    );
  }

  return (
    <MigrateSlp
      fullRange={fullRange}
      position={position}
      v2Vault={v2Vault}
      v3Vault={v3Vault}
      onCancel={handleCancel}
      onComplete={handleComplete}
      signRemove={signRemove}
      signRemoveError={signRemoveError}
      signRemoveStatus={signRemoveStatus}
      migrateError={migrateError}
      migrateSlp={migrateSlp}
      migrateStatus={migrateStatus}
      signData={signData}
      showXSlpSteps={!!showXSlpSteps}
    />
  );
};

export default MigrateSlpContainer;
